import { useEffect } from "react";
import { Box } from "@mui/material";
import Loading from "../common/Loading";
import useIsDesktop from "../hooks/useIsDesktop";
import ResourceSection from "./ResourceSection";
import { useGetResourcePage } from "services/resources";
import { PlacedSection } from "protogen/resources_service_pb";

export const RESOURCE_COLUMNS = [0, 1];

const renderSection = (placed: PlacedSection) => {
  const section = placed.section;
  if (!section) return null;
  return (
    <ResourceSection
      title={section.title}
      links={section.links.map((link) => ({
        name: link.name,
        href: link.href,
        icon: link.icon || undefined,
        targetNew: link.targetNew,
      }))}
    />
  );
};

export default ({ page }: { page: "resources" | "help" }) => {
  const isDesktop = useIsDesktop();
  const { data, loading, request } = useGetResourcePage();

  useEffect(() => {
    request({ page });
  }, [page]);

  if (loading && !data) return <Loading />;

  const placements = data?.placements || [];

  // Mobile: single column. Resources reads down each column (column-major); Help
  // reads row-by-row to match its desktop grid.
  if (!isDesktop) {
    const ordered = [...placements].sort((a, b) =>
      page === "resources"
        ? a.column - b.column || a.row - b.row
        : a.row - b.row || a.column - b.column,
    );
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "32px" }}>
        {ordered.map((cell) => (
          <Box key={`${cell.column}:${cell.row}`}>{renderSection(cell)}</Box>
        ))}
      </Box>
    );
  }

  // Resources: each column flows independently (masonry) so a tall section in one
  // column does not push down the other column.
  if (page === "resources") {
    return (
      <Box sx={{ display: "flex", flexDirection: "row", gap: "32px" }}>
        {RESOURCE_COLUMNS.map((column) => (
          <Box
            key={column}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "32px",
              width: "100%",
            }}
          >
            {placements
              .filter((cell) => cell.column === column)
              .sort((a, b) => a.row - b.row)
              .map((cell) => (
                <Box key={cell.row}>{renderSection(cell)}</Box>
              ))}
          </Box>
        ))}
      </Box>
    );
  }

  // Help: fixed row grid so empty boxes render as intentional gaps.
  const byCell = new Map<string, PlacedSection>();
  let maxRow = -1;
  placements.forEach((cell) => {
    byCell.set(`${cell.column}:${cell.row}`, cell);
    maxRow = Math.max(maxRow, cell.row);
  });
  const rows = Array.from({ length: maxRow + 1 }, (_, row) => row);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      {rows.map((row) => (
        <Box
          key={row}
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "32px",
            alignItems: "flex-start",
          }}
        >
          {RESOURCE_COLUMNS.map((column) => {
            const cell = byCell.get(`${column}:${row}`);
            return (
              <Box key={column} sx={{ width: "100%" }}>
                {cell ? renderSection(cell) : null}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};
